/** @module Apparel/ProductSize/_SizesRenderer */
define([
	"dojo/_base/declare",
	"underscore",
	"csi",
	"csi/action",
	"csi/action/Popup",
	"csi/def/ViewRef",
	"csi/heading/Refvector",
	"csi/locale",
	"csi/widget/TextWithMaterialIcon",
	"./SizeSet"
], function(declare, _, csi, csiAction, Popup, ViewRef, Refvector, locale, TextWithMaterialIcon, SizeSet) {

	/**
	 * Return comma separated list of ranges of sizes in set; if there are sizes in the set that are not within the given
	 * dimension 1 urls, then return an empty string. It does not make sense to have sizes in and others out of the given list.
	 * @param {Cache} cache
	 * @param {string[]} sizeUrls
	 * @param {string[]} allDim1Urls
	 * @param {string[]} deletedSizeUrls
	 * @return {string[]}
	 */
	function _getRangesInSizeSet(cache, sizeUrls, allDim1Urls, deletedSizeUrls) {
		const hashOfSizeUrls = csi.hashArray(sizeUrls, null, Boolean);
		const hashOfDeletedSizeUrls = csi.hashArray(deletedSizeUrls, null, Boolean);
		const sizeSet = new SizeSet(cache, sizeUrls);
		const is2d = sizeSet.has2dSizes();
		const ranges = [];
		let firstUrl;
		let lastUrl;

		const range = (url1, url2) => {
			if (url1 === url2) {
				return sizeSet.getSizeName(url1);
			}

			if (is2d) {
				url1 = cache.get(url1).Dimension1Size;
			}

			return locale.resource("RangeTemplate", [sizeSet.getSizeName(url1), sizeSet.getSizeName(url2)]);
		};

		const add = (url) => {
			if (hashOfSizeUrls[url]) {
				firstUrl = firstUrl || url;
				lastUrl = url;
				delete hashOfSizeUrls[url];
				delete hashOfDeletedSizeUrls[url];
			} else {
				if (firstUrl) {
					ranges.push(range(firstUrl, lastUrl));
				}

				firstUrl = lastUrl = null;
			}
		};

		/**
		 * add size prefix, suffix range  in the ranges 
		 */
		const addPrefixSuffixSizeRange = (size) => {
			ranges.push(sizeSet.getSizeName(size.$URL));
			delete hashOfDeletedSizeUrls[size.$URL];
		};

		/**
		 * collect all prefix/suffix sizes for supplied sizeurl from deleted size urls 
		 *	for prefix/suffix - it compares the SizeCode and prepare the list
		 */
		const prefixSuffixSizeRanges = (dim1SizeUrl, isPrefix) => {
			if (_.isEmpty(deletedSizeUrls)) {
				return;
			}
			const dim1Size = cache.get(dim1SizeUrl);
			for (let deletedSizeUrl of deletedSizeUrls) {
				if (hashOfDeletedSizeUrls[deletedSizeUrl]) {
					const deletedSize = cache.getNode(deletedSizeUrl);
					if (isPrefix) {
						if (deletedSize.SizeCode <= dim1Size.SizeCode) {
							addPrefixSuffixSizeRange(deletedSize);
						}
					} else {
						if (deletedSize.SizeCode >= dim1Size.SizeCode) {
							addPrefixSuffixSizeRange(deletedSize);
						}
					}
				}
			}
		};

		/**
		 * collect all prefix/suffix 2d sizes for supplied sizeurls from deleted size urls 
		 * for prefix/suffix - it compares dim1Size.SizeCode and dim2Size.SizeCode and prepare the list	
		 */
		const prefixSuffix2dSizeRanges = (dim1SizeUrl, dim2SizeUrl, isPrefix) => {
			if (_.isEmpty(deletedSizeUrls)) {
				return;
			}
			const dim1Size = cache.get(dim1SizeUrl);
			const dim2Size = cache.get(dim2SizeUrl);

			for (let deletedSizeUrl of deletedSizeUrls) {
				if (hashOfDeletedSizeUrls[deletedSizeUrl]) {
					const deletedSize = cache.getNode(deletedSizeUrl);
					const dsize1 = cache.get(deletedSize.Dimension1Size);
					const dsize2 = cache.get(deletedSize.Dimension2Size);
					if (isPrefix) {
						if (dsize2.SizeCode < dim2Size.SizeCode) {
							addPrefixSuffixSizeRange(deletedSize);
						} else if (dsize2.SizeCode === dim2Size.SizeCode) {
							if (dsize1.SizeCode <= dim1Size.SizeCode) {
								addPrefixSuffixSizeRange(deletedSize);
							}
						}
					} else {
						if (dsize2.SizeCode === dim2Size.SizeCode) {
							if (dsize1.SizeCode >= dim1Size.SizeCode) {
								addPrefixSuffixSizeRange(deletedSize);
							}
						} else if (dsize2.SizeCode > dim2Size.SizeCode) {
							addPrefixSuffixSizeRange(deletedSize);
						}
					}
				}
			}
		};

		if (is2d) {
			let url1;
			let url2;
			// for each dimension 2 size, iterate through all dimension 1 sizes
			for (url2 of sizeSet.dim2SizeUrls) {
				let isPrefix2dSizeRangesCalled = false;
				for (url1 of sizeSet.dim1SizeUrls) {
					if (!isPrefix2dSizeRangesCalled) {
						prefixSuffix2dSizeRanges(url1, url2, true);
						isPrefix2dSizeRangesCalled = true;
					}
					add(sizeSet.get2dSizeUrl(url1, url2));
				}
				add(null);
			}
			prefixSuffix2dSizeRanges(url1, url2, false);
		} else {
			let isPrefixSizeRangesCalled = false;
			let url;
			// iterate through all dimension 1 sizes
			for (url of allDim1Urls) {
				if (!isPrefixSizeRangesCalled) {
					prefixSuffixSizeRanges(url, true);
					isPrefixSizeRangesCalled = true;
				}
				add(url);
			}

			add(null);

			prefixSuffixSizeRanges(url, false);
		}

		// this handles case where there are sizes that do not exist in the size range (or no size range), which is possible
		// when sizes are removed from the global size range, or on a BOM after the product size range changes
		return _.isEmpty(hashOfSizeUrls) ? ranges : [];
	}

	return declare(Refvector, {
		/**
		 * Extension point to return size range node where sizes came from
		 * @param {Context} ctx
		 * @return {C8Node} - could be of type SizeRange or also adapted to work with SizeChartRevision
		 */
		getSizeRange: function(ctx) {
			return null;
		},

		/**
		 * Return urls for dimension 1 of size range
		 * @param {csi/view/Widget} view
		 * @param {C8Node} sizeRange
		 * @param {SizeSet} sizeRangeSizeSet
		 * @return {string[]}
		 */
		getSizeRangeDimension1Urls: function(view, sizeRange, sizeRangeSizeSet) {
			const {cacheView} = view;
			const {SizeChartSubranges, SizeChartSubSizeRanges} = sizeRange;
			const srs = cacheView.getNodes(SizeChartSubranges || SizeChartSubSizeRanges);

			if (srs.length) {
				const activeSrs = srs.filter(r => r.Active);

				// for composite size range, insert empty url in between sub size ranges
				return activeSrs.reduce((memo, sr) => {
					if (memo.length) {
						memo.push(csi.EMPTY_URL);
					}

					return memo.concat(sr.Sizes);
				}, []);
			}

			return sizeRangeSizeSet.dim1SizeUrls;
		},

		/**
		 * Return size set for size range by default, but allow for override in Size Range table to show 2d sizes available
		 * in the system that are not chosen for the size range
		 * @param {csi/view/Widget} view
		 * @param {C8Node} sizeRange
		 * @return {SizeSet}
		 */
		getSizeRangeSizeSet: function(view, sizeRange) {
			return new SizeSet(view.cacheView, this.getSizes(view, sizeRange));
		},

		/**
		 * Return product size urls referenced by attribute in given view
		 * @param {ViewWidget} view
		 * @param {SizeRange} sizeRange
		 * @return {string[]}
		 */
		getSizes: function(view, sizeRange) {
			if (!sizeRange) {
				return []; // no sizes if no size range
			}
			if (sizeRange.SizeChartSubSizeRanges) {
				let activeSizes = [];
				view.cacheView.getNodes(sizeRange.SizeChartSubSizeRanges).forEach(function(sr) {
					if (sr.Active) {
						activeSizes = activeSizes.concat(sr.Sizes);
					}
				});
				return activeSizes;
			}

			return sizeRange.Sizes;
		},

		/**
		 * @override
		 * Render selected sizes in subranges rather than individually
		 */
		toHtmlStatic: function(ctx, h, refNode, actionData) {
			const sizeRange = this.getSizeRange(ctx);
			if (!sizeRange) {
				return this.inherited(arguments);
			}

			const {view} = ctx;
			const allSelectedSizes = this._getValue(ctx, h);
			const sizeUrls = _.intersection(this.getSizes(view, sizeRange), allSelectedSizes);
			const deletedSizesUrls = _.difference(allSelectedSizes, sizeUrls);
			const sizeRangeSizeSet = this.getSizeRangeSizeSet(view, sizeRange);
			const dim1Urls = this.getSizeRangeDimension1Urls(view, sizeRange, sizeRangeSizeSet);
			const ranges = dim1Urls.length ? _getRangesInSizeSet(ctx.cache, sizeUrls, dim1Urls, deletedSizesUrls) : [];
			const last = ranges.pop();

			if (last && !view.isPreview) {
				const suffix = "," + (ctx.bExcel ? " " : "");
				const text = ranges.map(s => s + suffix);
				const selectedSizes = new Set();

				text.push(last);

				sizeUrls.forEach(url => selectedSizes.add(url));

				new TextWithMaterialIcon({
					icon: (ctx.bExcel || ctx.bPrint) ? "" : "open_in_new",
					text,
					onClick: () => {
						const viewParams = {
							dim1Urls,
							selectedSizes,
							sizeSet: sizeRangeSizeSet
						};

						const action = _.extend(new Popup(), {
							m_id: "PopupSizesInRange",
							m_viewRef: _.extend(new ViewRef(), {
								m_id: "SizesInRange",
								m_url: sizeRange.$URL
							})
						});

						action.resolve();

						csiAction.invoke(view, refNode, action, {
							getTitle: () => h.getLabel(),
							onCreatePopupParams: params => _.extend(params, viewParams)
						});
					}
				}).placeAt(refNode);
				return true;
			}

			return this.inherited(arguments);
		},

		/**
		 * @override
		 * Use custom CSS for this cell
		 */
		_getCSS: function(type, format) {
			return "csi-size-ranges";
		}
	});
});
